import { Link } from '@tanstack/react-router';
import IconDisposable from 'assets/icons/disposable.svg?react';
import IconRecurring from 'assets/icons/recurring.svg?react';
import IconOverlapping from 'assets/icons/overlapping.svg?react';
import IconMarket from 'assets/icons/market.svg?react';
import IconRange from 'assets/icons/range.svg?react';
import ArrowCircleRightIcon from 'assets/icons/arrow_circle_right.svg?react';
import IconShield from 'assets/icons/shield.svg?react';
import TargetIcon from 'assets/icons/target.svg?react';
import AuctionIcon from 'assets/icons/auction.svg?react';
import ChannelIcon from 'assets/icons/channel.svg?react';
import { useTrending } from 'libs/queries/extApi/tradeCount';
import { useMemo } from 'react';
import { prettifyNumber } from 'utils/helpers';
import { RollingNumber } from 'components/common/RollingNumber';
import { useGetAllStrategies } from 'libs/queries';
import { RewardBanner } from 'components/rewards/banner';

const types = [
  {
    title: 'Essentials',
    description:
      'The foundation of every DEX: core features for trading and liquidity',
    trades: [
      {
        name: 'Swap',
        to: '/trade/market' as const,
        search: {},
        icon: <IconMarket className="size-24" />,
      },
      {
        name: 'Full Range Liquidity',
        to: '/trade/overlapping' as const,
        search: { preset: 'Infinity' } as const,
        icon: <IconOverlapping className="size-24" />,
      },
      {
        name: 'Concentrated Liquidity',
        to: '/trade/overlapping' as const,
        search: {},
        icon: <IconOverlapping className="size-24" />,
      },
    ],
    targets: ['Casual', 'Newbies'],
  },
  {
    title: 'Intermediate',
    description:
      'Tailored trading options for greater precision and absolute price certainty',
    trades: [
      {
        name: 'Limit order',
        to: '/trade/disposable' as const,
        search: { settings: 'limit' as const },
        icon: <IconDisposable className="size-24" />,
      },
      {
        name: 'Recurring limit orders',
        to: '/trade/recurring' as const,
        search: { buySettings: 'limit', sellSettings: 'limit' } as const,
        icon: <IconRecurring className="size-24" />,
      },
      {
        name: 'Timed Auction',
        to: '/trade/auction' as const,
        search: { direction: 'buy' } as const,
        icon: <AuctionIcon className="size-24" />,
      },
    ],
    targets: ['Advanced', 'Experts'],
  },
  {
    title: 'Advanced',
    description:
      'Customizable strategies for scaling, and automating buy low, sell high trades - unique to Carbon DeFi.',
    trades: [
      {
        name: 'Range order',
        to: '/trade/disposable' as const,
        search: { settings: 'range' as const },
        icon: <IconRange className="size-24" />,
      },
      {
        name: 'Recurring range orders',
        to: '/trade/recurring' as const,
        icon: <IconRecurring className="size-24" />,
      },
      {
        name: 'Recurring Timed Auction',
        to: '/trade/triangle' as const,
        search: {} as const,
        icon: <ChannelIcon className="size-24" />,
      },
    ],
    targets: ['Professional', 'Quants'],
  },
];

export const UnconnectedLandingPage = () => {
  const trending = useTrending();
  const strategies = useGetAllStrategies({ enabled: true });
  const sentence = useMemo(() => {
    if (trending.isPending || strategies.isPending) {
      return (
        <p className="invisible font-title text-xl md:text-3xl">
          Hide to prevent layout shift
        </p>
      );
    }
    const trades = trending.data?.totalTradeCount;
    const totalStrategies = strategies.data?.length;
    if (!trades || !totalStrategies) return;
    const format = (value: number) => {
      return prettifyNumber(value, { isInteger: true });
    };
    return (
      <div className="font-title flex flex-col items-center gap-8 justify-center text-xl md:flex-row md:items-baseline md:text-3xl">
        <RollingNumber
          className="text-xl md:text-3xl"
          value={totalStrategies}
          loadingWidth="1ch"
          format={format}
        />
        <span>trading strategies powering</span>
        <RollingNumber
          className="text-xl md:text-3xl"
          value={trades}
          loadingWidth="1ch"
          format={format}
          initDelta={60}
        />
        <span>trades</span>
      </div>
    );
  }, [
    strategies.data?.length,
    strategies.isPending,
    trending.data?.totalTradeCount,
    trending.isPending,
  ]);

  return (
    <section className="grid content-start gap-24 py-16">
      <div className="grid gap-24 justify-self-center px-16">
        <hgroup className="grid gap-24">
          <h1 className="text-3xl lg:text-5xl text-center text-gradient leading-[1.5] gradient-direction-[90deg]">
            Control Your Trading Strategies
          </h1>
          {sentence}
        </hgroup>
        <ol className="grid gap-8">
          <li className="flex items-center gap-16 bg-main-900/20 px-16 py-8 rounded-md">
            <b className="text-gradient text-nowrap">Step 1</b>
            <span>Connect your wallet</span>
          </li>
          <li className="flex items-center gap-16 bg-main-900/20 px-16 py-8 rounded-md">
            <b className="text-gradient text-nowrap">Step 2</b>
            <span>Create your trading strategy</span>
          </li>
          <li className="flex items-center gap-16 bg-main-900/20 px-16 py-8 rounded-md">
            <b className="text-gradient text-nowrap">Step 3</b>
            <span>
              Sit back and let the market come to you - buy and sell on your
              terms
            </span>
          </li>
        </ol>
      </div>
      <RewardBanner />
      <article className="grid gap-16 justify-self-center px-16">
        <h2 className="text-18">Choose Your Trading Strategy</h2>
        <ul className="flex sm:justify-center flex-wrap gap-24">
          {types.map((item) => (
            <li
              className="flex flex-col gap-16 p-24 surface sm:w-[300px] rounded-2xl"
              key={item.title}
            >
              <h3 className="text-xl">{item.title}</h3>
              <p className="text-14 text-main-0/60 flex-1">
                {item.description}
              </p>
              <Link
                to="/explore/strategies"
                className="flex items-center gap-8"
              >
                <span className="text-gradient gradient-direction-[90deg] font-medium">
                  Explore strategies
                </span>
                <ArrowCircleRightIcon className="size-24 fill-gradient stroke-gradient" />
              </Link>
              <nav aria-label="strategy types" className="grid gap-8">
                {item.trades.map((trade) => (
                  <Link
                    to={trade.to}
                    search={trade.search}
                    key={trade.name}
                    className="btn-on-surface flex items-center gap-16 px-16 py-16 text-14"
                  >
                    {trade.icon}
                    <span>{trade.name}</span>
                  </Link>
                ))}
              </nav>
              <ul className="flex gap-8">
                {item.targets.map((target) => (
                  <span
                    key={target}
                    className="px-8 py-4 text-12 bg-main-600/40 text-main-0/40 rounded-md"
                  >
                    {target}
                  </span>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </article>
      <article className="grid gap-24 px-16">
        <h2 className="text-center text-3xl">Carbon DeFi User Benefits</h2>
        <ul className="flex justify-center flex-wrap gap-40">
          <li className="flex gap-8 items-center">
            <IconShield className="size-20" />
            <span>MEV Sandwich Attack Immunity</span>
          </li>
          <li className="flex gap-8 items-center">
            <TargetIcon className="size-24" />
            <span>100% Price Certainty</span>
          </li>
          <li className="flex gap-8 items-center">
            <IconDisposable className="size-20" />
            <span>Multi-Order Strategies</span>
          </li>
        </ul>
      </article>
    </section>
  );
};
